<template>
  <section class="wrap">
    <h2>工作台（数据统计看板）</h2>
    <p class="sub">待办事项可点击进入对应处理页面；会员数据区展示整体运营指标。</p>
    <p v-if="errorMsg" class="error">{{ errorMsg }}</p>

    <h3 class="section-title">待办事项</h3>
    <div class="todo-grid">
      <button
        v-for="card in todoCards"
        :key="card.key"
        type="button"
        class="todo-card"
        :class="card.accent"
        @click="go(card)"
      >
        <span class="todo-label">{{ card.label }}</span>
        <span class="todo-value">{{ card.value }}</span>
      </button>
    </div>

    <h3 class="section-title">会员数据</h3>
    <div class="stats-grid">
      <button
        v-for="card in memberCards"
        :key="card.key"
        type="button"
        class="stat-card"
        :class="card.accent"
        @click="go(card)"
      >
        <div class="stat-label">{{ card.label }}</div>
        <div class="stat-value">{{ card.value }}</div>
      </button>
    </div>

    <div class="chart-grid">
      <div class="chart-card">
        <div class="chart-title">会员核心指标</div>
        <div ref="overviewChartRef" class="chart-box"></div>
      </div>
      <div class="chart-card">
        <div class="chart-title">会员状态分布</div>
        <div ref="entryChartRef" class="chart-box"></div>
      </div>
    </div>

    <div class="section-head">
      <h3 class="section-title">会员活跃（近 30 天）</h3>
      <button type="button" class="link-detail" @click="goEngagement">查看活跃会员明细 →</button>
    </div>
    <div class="stats-grid engagement-stats">
      <div v-for="card in engagementCards" :key="card.key" class="stat-card accent-teal">
        <div class="stat-label">{{ card.label }}</div>
        <div class="stat-value">{{ card.value }}</div>
      </div>
    </div>
    <div class="chart-grid">
      <div class="chart-card">
        <div class="chart-title">活跃趋势</div>
        <div ref="engagementTrendRef" class="chart-box"></div>
      </div>
      <div class="chart-card">
        <div class="chart-title">行为构成</div>
        <div ref="engagementCompositionRef" class="chart-box"></div>
      </div>
    </div>
  </section>
</template>

<script>
import {
  getAdminStatsOverview,
  getAdminEngagementOverview,
  getAdminEngagementTrend,
  getAdminEngagementComposition,
} from '@/api'
import * as echarts from 'echarts'
import { ENGAGEMENT_CHART_COLORS, engagementGrid, formatRangeParams } from '@/utils/engagementCharts.js'

export default {
  name: 'DashboardView',
  data() {
    return {
      errorMsg: '',
      overviewChart: null,
      entryChart: null,
      engagementTrendChart: null,
      engagementCompositionChart: null,
      engagementOverview: null,
      engagementTrend: null,
      engagementComposition: null,
      stats: {
        bannersEnabled: 0,
        supplyPostsPendingReview: 0,
        membersTotal: 0,
        membersActive: 0,
        membersRegistered: 0,
        membershipApplyPendingReview: 0,
        membershipFeePendingReview: 0,
        renewalFeePendingReview: 0,
        newRegistrants: 0,
        memberOrdersTotal: 0,
        memberActivityRegsTotal: 0,
      },
    }
  },
  computed: {
    todoCards() {
      const s = this.stats
      return [
        {
          key: 'apply',
          label: '入会审核',
          value: s.membershipApplyPendingReview,
          path: '/form/submissions',
          query: { reviewFilter: '待审核申请表', returnTo: '/dashboard' },
          accent: 'accent-orange',
        },
        {
          key: 'join-fee',
          label: '入会缴费审核',
          value: s.membershipFeePendingReview,
          path: '/form/submissions',
          query: { reviewFilter: '待审核入会缴费', returnTo: '/dashboard' },
          accent: 'accent-cyan',
        },
        {
          key: 'renewal',
          label: '续费审核',
          value: s.renewalFeePendingReview,
          path: '/member/manage',
          query: { feeStatus: 'REVIEWING' },
          accent: 'accent-violet',
        },
        {
          key: 'new-user',
          label: '新用户注册',
          value: s.newRegistrants,
          path: '/member/map-profiles',
          query: { status: 'REGISTERED' },
          accent: 'accent-blue',
        },
        {
          key: 'supply',
          label: '供需待审核',
          value: s.supplyPostsPendingReview,
          path: '/supply/review',
          query: {},
          accent: 'accent-teal',
        },
      ]
    },
    memberCards() {
      const s = this.stats
      return [
        {
          key: 'total',
          label: '会员总数',
          value: s.membersTotal,
          path: '/member/map-profiles',
          query: {},
          accent: 'accent-blue',
        },
        {
          key: 'active',
          label: '正式会员',
          value: s.membersActive,
          path: '/member/manage',
          query: {},
          accent: 'accent-indigo',
        },
        {
          key: 'registered',
          label: '注册用户',
          value: s.membersRegistered,
          path: '/member/map-profiles',
          query: { status: 'REGISTERED' },
          accent: 'accent-cyan',
        },
        {
          key: 'orders',
          label: '会员订单总数',
          value: s.memberOrdersTotal,
          path: '/member/manage',
          query: {},
          accent: 'accent-violet',
        },
        {
          key: 'activity',
          label: '活动报名总数',
          value: s.memberActivityRegsTotal,
          path: '/content/activities',
          query: {},
          accent: 'accent-green',
        },
      ]
    },
    engagementCards() {
      const o = this.engagementOverview || {}
      return [
        { key: 'active', label: '活跃用户数', value: o.activeUserCount ?? 0 },
        { key: 'checkin', label: '签到人次', value: o.checkinEventCount ?? 0 },
        { key: 'viewUsers', label: '浏览人数', value: o.activityViewUserCount ?? 0 },
        { key: 'viewEvents', label: '浏览次数', value: o.activityViewEventCount ?? 0 },
        { key: 'surveyUsers', label: '问卷人数', value: o.surveyUserCount ?? 0 },
        { key: 'surveyEvents', label: '问卷份数', value: o.surveySubmitCount ?? 0 },
      ]
    },
  },
  mounted() {
    this.initCharts()
    this.load()
    window.addEventListener('resize', this.handleResize)
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize)
    if (this.overviewChart) this.overviewChart.dispose()
    if (this.entryChart) this.entryChart.dispose()
    if (this.engagementTrendChart) this.engagementTrendChart.dispose()
    if (this.engagementCompositionChart) this.engagementCompositionChart.dispose()
  },
  methods: {
    goEngagement() {
      this.$router.push({ path: '/member/engagement' }).catch(() => {})
    },
    go(card) {
      if (!card || !card.path) return
      this.$router.push({ path: card.path, query: card.query || {} }).catch(() => {})
    },
    initCharts() {
      if (this.$refs.overviewChartRef) {
        this.overviewChart = echarts.init(this.$refs.overviewChartRef)
      }
      if (this.$refs.entryChartRef) {
        this.entryChart = echarts.init(this.$refs.entryChartRef)
      }
      if (this.$refs.engagementTrendRef) {
        this.engagementTrendChart = echarts.init(this.$refs.engagementTrendRef)
      }
      if (this.$refs.engagementCompositionRef) {
        this.engagementCompositionChart = echarts.init(this.$refs.engagementCompositionRef)
      }
      this.renderCharts()
    },
    handleResize() {
      if (this.overviewChart) this.overviewChart.resize()
      if (this.entryChart) this.entryChart.resize()
      if (this.engagementTrendChart) this.engagementTrendChart.resize()
      if (this.engagementCompositionChart) this.engagementCompositionChart.resize()
    },
    async load() {
      try {
        const range = formatRangeParams('30', '', '')
        const [data, overview, trend, composition] = await Promise.all([
          getAdminStatsOverview(),
          getAdminEngagementOverview(range),
          getAdminEngagementTrend({ ...range, granularity: 'day' }),
          getAdminEngagementComposition(range),
        ])
        this.stats = { ...this.stats, ...(data || {}) }
        this.engagementOverview = overview || {}
        this.engagementTrend = trend || { points: [] }
        this.engagementComposition = composition || { slices: [] }
        this.renderCharts()
      } catch (e) {
        this.errorMsg = e.message || '读取统计失败'
      }
    },
    renderCharts() {
      if (this.overviewChart) {
        this.overviewChart.setOption({
          color: ['#2f6af6'],
          tooltip: { trigger: 'axis' },
          grid: { left: 36, right: 16, top: 36, bottom: 28 },
          xAxis: {
            type: 'category',
            axisTick: { show: false },
            axisLine: { lineStyle: { color: '#e5e7eb' } },
            axisLabel: { color: '#667085' },
            data: ['会员总数', '正式会员', '注册用户', '会员订单', '活动报名'],
          },
          yAxis: {
            type: 'value',
            splitLine: { lineStyle: { color: '#eef2f7' } },
            axisLabel: { color: '#98a2b3' },
          },
          series: [{
            data: [
              this.stats.membersTotal,
              this.stats.membersActive,
              this.stats.membersRegistered,
              this.stats.memberOrdersTotal,
              this.stats.memberActivityRegsTotal,
            ],
            type: 'bar',
            barWidth: 28,
            itemStyle: { borderRadius: [8, 8, 0, 0] },
          }],
        })
      }

      if (this.entryChart) {
        this.entryChart.setOption({
          color: ['#4f7cff', '#22c3ee', '#8b5cf6'],
          tooltip: { trigger: 'item' },
          legend: {
            bottom: 0,
            icon: 'circle',
            textStyle: { color: '#667085' },
          },
          series: [{
            type: 'pie',
            radius: ['48%', '72%'],
            center: ['50%', '45%'],
            avoidLabelOverlap: false,
            label: { show: true, formatter: '{b}\n{c}' },
            labelLine: { show: false },
            data: [
              { value: this.stats.membersActive, name: '正式会员' },
              { value: this.stats.membersRegistered, name: '注册用户' },
              {
                value: Math.max(this.stats.membersTotal - this.stats.membersActive - this.stats.membersRegistered, 0),
                name: '其它状态',
              },
            ],
          }],
        })
      }

      const points = this.engagementTrend?.points || []
      if (this.engagementTrendChart) {
        this.engagementTrendChart.setOption({
          color: ENGAGEMENT_CHART_COLORS,
          tooltip: { trigger: 'axis' },
          legend: { data: ['活跃用户数', '行为次数'], bottom: 0, textStyle: { color: '#667085' } },
          grid: { ...engagementGrid(), bottom: 48 },
          xAxis: { type: 'category', data: points.map((p) => p.label), axisLabel: { color: '#667085' } },
          yAxis: { type: 'value', splitLine: { lineStyle: { color: '#eef2f7' } } },
          series: [
            { name: '活跃用户数', type: 'line', smooth: true, data: points.map((p) => p.activeUserCount) },
            { name: '行为次数', type: 'bar', barWidth: 10, data: points.map((p) => p.behaviorCount) },
          ],
        })
      }

      const slices = this.engagementComposition?.slices || []
      if (this.engagementCompositionChart) {
        this.engagementCompositionChart.setOption({
          color: ENGAGEMENT_CHART_COLORS,
          tooltip: { trigger: 'item' },
          legend: { bottom: 0, icon: 'circle', textStyle: { color: '#667085' } },
          series: [{
            type: 'pie',
            radius: ['48%', '72%'],
            center: ['50%', '45%'],
            data: slices.map((s) => ({ name: s.name, value: s.value })),
            label: { formatter: '{b}\n{c}' },
          }],
        })
      }
    },
  },
}
</script>

<style scoped>
.wrap { background: #fff; border-radius: 12px; padding: 18px; }
h2 { margin-top: 0; }
.sub { color: #667085; margin-top: 4px; }
.error { color: #d92d20; }
.section-title {
  margin: 18px 0 10px;
  font-size: 15px;
  color: #344054;
}
.todo-grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 12px;
}
.todo-card {
  position: relative;
  overflow: hidden;
  text-align: left;
  cursor: pointer;
  border: 1px solid #e6edf8;
  border-radius: 12px;
  padding: 14px;
  background: linear-gradient(135deg, #ffffff 0%, #f8fbff 100%);
  transition: transform 0.12s ease, box-shadow 0.12s ease;
}
.todo-card:hover {
  transform: translateY(-1px);
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.08);
}
.todo-card::after {
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  width: 72px;
  height: 72px;
  border-radius: 0 0 0 72px;
  opacity: .08;
}
.todo-label { display: block; color: #2a4b8d; font-size: 12px; }
.todo-value { display: block; font-size: 28px; font-weight: 800; margin-top: 6px; color: #0b2a5b; }
.stats-grid { display: grid; grid-template-columns: repeat(5, minmax(0, 1fr)); gap: 12px; }
.stat-card {
  position: relative;
  overflow: hidden;
  text-align: left;
  cursor: pointer;
  background: linear-gradient(135deg, #ffffff 0%, #f8fbff 100%);
  border: 1px solid #e6edf8;
  border-radius: 12px;
  padding: 14px;
  transition: transform 0.12s ease, box-shadow 0.12s ease;
}
.stat-card:hover {
  transform: translateY(-1px);
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.08);
}
.stat-card::after {
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  width: 72px;
  height: 72px;
  border-radius: 0 0 0 72px;
  opacity: .08;
}
.stat-label { color: #2a4b8d; font-size: 12px; }
.stat-value { font-size: 24px; font-weight: 800; margin-top: 6px; color: #0b2a5b; }
.accent-blue::after { background: #2f6af6; }
.accent-indigo::after { background: #5b6cff; }
.accent-cyan::after { background: #22c3ee; }
.accent-violet::after { background: #8b5cf6; }
.accent-green::after { background: #12b76a; }
.accent-teal::after { background: #14b8a6; }
.accent-orange::after { background: #f79009; }
.chart-grid { display: grid; grid-template-columns: 1.6fr 1fr; gap: 12px; margin-top: 14px; }
.chart-card {
  background: #fff;
  border: 1px solid #e9eef5;
  border-radius: 14px;
  padding: 14px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.04);
}
.chart-title { font-size: 14px; font-weight: 700; color: #1d2939; margin-bottom: 8px; }
.chart-box { width: 100%; height: 320px; }
.section-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 18px;
}
.section-head .section-title { margin: 0; }
.engagement-stats { margin-top: 10px; }
.link-detail {
  border: none;
  background: none;
  color: #2f6af6;
  cursor: pointer;
  font-size: 13px;
  padding: 0;
}
@media (max-width: 1200px) {
  .todo-grid, .stats-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
}
@media (max-width: 768px) {
  .todo-grid, .stats-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .chart-grid { grid-template-columns: 1fr; }
}
</style>
